import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot } from 'react-icons/fa';
import axios from 'axios';
import { validatePhoneNumber, formatPhoneNumber, phoneErrorMsg } from '../utils/phoneUtils';
import { getIndustrySlug, validateIndustry, industryErrorMsg } from '../utils/industryUtils';
import './ChatBot.css';

// Configure axios instances
const chatApi = axios.create({
  baseURL: 'https://us-central1-callmanager-ai.cloudfunctions.net/chat',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const callDemoApi = axios.create({
  baseURL: 'https://calldemo-nsqvlwjt7a-uc.a.run.app',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

const ChatBot = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hi 👋 I'm Kleo, your Solutions Advisor. How can I help today?" }
  ]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [callStatus, setCallStatus] = useState('');
  const [callId, setCallId] = useState('');
  const [agentId, setAgentId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const userId = 'user-' + Math.random().toString(36).substring(2, 9);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const newMsg = { from: 'user', text: input };
    setMessages(prev => [...prev, newMsg]);
    setInput('');
    setTyping(true);

    try {
      const response = await chatApi.post('', {
        message: input,
        userId: userId
      });

      if (response.data && response.data.response) {
        setMessages(prev => [...prev, { 
          from: 'bot', 
          text: response.data.response 
        }]);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('ChatBot error:', err);
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: 'Sorry, I encountered an error. Please try again later.' 
      }]);
    } finally {
      setTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') sendMessage();
  };

  const handleDemoCall = async () => {
    const phoneInput = phoneNumber;
    const formattedPhone = formatPhoneNumber(phoneInput);
    const industrySlug = getIndustrySlug(selectedIndustry);

    if (!validatePhoneNumber(formattedPhone)) {
      setMessages(prev => [...prev, { from: 'bot', text: `⚠️ ${phoneErrorMsg}` }]);
      return;
    }
    if (!validateIndustry(selectedIndustry)) {
      setMessages(prev => [...prev, { from: 'bot', text: `⚠️ ${industryErrorMsg}` }]);
      return;
    }

    try {
      setTyping(true);
      setErrorMessage('');
      
      const response = await callDemoApi.post('/callDemo', 
        { 
          to: formattedPhone,
          useCase: industrySlug 
        },
        {
          timeout: 10000,
          retry: 2,
          retryDelay: 2000
        }
      );

      if (response.data.call_id && response.data.agent_id) {
        setCallStatus('success');
        setCallId(response.data.call_id);
        setAgentId(response.data.agent_id);
        setMessages(prev => [...prev, { 
          from: 'bot', 
          text: "✅ Call initiated successfully! Kleo is calling you now." 
        }]);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('[Call API Error]', {
        error: err.message,
        config: err.config,
        response: err.response?.data
      });
      
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: `❌ ${err.response?.data?.error || 'Failed to initiate call. Please try again later.'}` 
      }]);
      setCallStatus('error');
      setErrorMessage(err.response?.data?.error || 'Failed to initiate call');
    } finally {
      setTyping(false);
    }
  };

  return (
    <>
      {/* Launcher Button */}
      <motion.div 
        className="chat-launcher"
        onClick={onToggle}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <FaRobot size={20} color="#fff" />
        <motion.div 
          className="chat-tooltip"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          Chat with Kleo
        </motion.div>
      </motion.div>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="chat-container"
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <div className="chat-header">
              <h3>Kleo AI</h3>
              <button onClick={onToggle} className="chat-close">×</button>
            </div>

            <div className="chat-messages">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  className={`chat-message ${msg.from}`}
                  initial={{ opacity: 0, x: msg.from === 'user' ? 30 : -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="chat-avatar">
                    {msg.from === 'bot' 
                      ? <img src="/pictures/chat-icon.png" alt="Chat Icon" className="chat-avatar" /> 
                      : <div className="user-icon">👤</div>}
                  </div>
                  <div className="chat-bubble">{msg.text}</div>
                </motion.div>
              ))}
              {typing && (
                <div className="typing-indicator">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              )}
            </div>

            <div className="chat-input">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask Kleo anything..."
              />
              <button onClick={sendMessage}>Send</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
